import styled from '@emotion/styled';
import { useMediaQuery } from '@react-hook/media-query';
import React, { FC } from 'react';
import { mediaQuery } from 'src/shared/ds/variables';
import { pxToRem } from 'src/shared/helpers/pxToRem';

export interface GridProps {
    lgMode?: keyof typeof lgModeConfig;
}

const lgModeConfig = {
    '5columns': {
        columns: 5,
        gap: 16,
    },
    '3columns': {
        columns: 3,
        gap: 32,
    },
};

export const Grid: FC<GridProps> = ({ children, lgMode = '5columns' }) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    const mediumScreenDetected = useMediaQuery(mediaQuery.maxWidth('xl', true));
    const config = lgModeConfig[lgMode];

    const chooseColumnsNumber = () => {
        if (smallScreenDetected) {
            return 1;
        }
        if (mediumScreenDetected) {
            return 4;
        }

        return config.columns;
    };

    return (
        <Container columns={chooseColumnsNumber()} gap={config.gap}>
            {children}
        </Container>
    );
};

const Container = styled.div<{ columns: number; gap: number }>(({ columns, gap }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr);`,
    gridColumnGap: pxToRem(gap),
    gridRowGap: pxToRem(24),
}));

export interface GridElementProps {
    md: number;
    lg: number;
    column?: boolean;
    hide?: 'md' | 'sm';
}

export const GridItem: FC<GridElementProps> = ({ children, lg, md, column, hide }) => {
    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('md', true));
    const mediumScreenDetected = useMediaQuery(mediaQuery.maxWidth('xl', true));

    const chooseSpan = () => {
        if (smallScreenDetected) {
            return 1;
        }
        if (mediumScreenDetected) {
            return md;
        }

        return lg;
    };

    if (hide && hide === 'md' && mediumScreenDetected) {
        return null;
    }

    if (hide && hide === 'sm' && smallScreenDetected) {
        return null;
    }

    return (
        <ItemContainer span={chooseSpan()} css={[column && { flexDirection: 'column' }]}>
            {children}
        </ItemContainer>
    );
};

const ItemContainer = styled.div<{ span: number }>(({ span }) => ({
    gridColumnEnd: `span ${span}`,
    display: 'flex',
}));
